// src/features/executive/insight/widgets/AgeGroupBar.tsx
import { Chip, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import CategoricalBar from './CategoricalBar'
import { useAgeGroup } from '../hooks/use-age-group'
import { formatCount, formatVariance } from 'utils/executive.utils'

// When a large share of patient records is missing dateOfBirth, the age
// histogram silently undercounts the cohort — flag the data-quality issue
// so admins can patch the register form / backfill DOB.
const UNKNOWN_DOB_WARNING_PCT = 10

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function AgeGroupBar() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useAgeGroup()

  const rows =
    data?.segments.map((s) => ({
      key: s.key,
      label: s.label, // already ordinal label (<18, 18-30, ...); no translation needed
      count: s.count,
      percent: s.percent,
      previousCount: s.previousCount,
    })) ?? []

  const totalKnown = data?.total ?? 0
  const unknown = data?.unknownCount ?? 0
  const totalAll = totalKnown + unknown
  const pctUnknown = totalAll > 0 ? (unknown / totalAll) * 100 : 0
  const showUnknownWarning = pctUnknown > UNKNOWN_DOB_WARNING_PCT

  const totalVariance = varianceOf(totalKnown, data?.previousTotal)

  const patientUnit = t('EXECUTIVE.INSIGHT.UNIT.PATIENT', 'ราย')

  const subtitle = data ? (
    <>
      {formatCount(totalKnown, patientUnit)}
      {totalVariance != null && (
        <Typography
          component="span"
          variant="caption"
          color={totalVariance >= 0 ? 'success.main' : 'error.main'}
          sx={{ ml: 0.75 }}
        >
          {formatVariance(totalVariance)}
        </Typography>
      )}
    </>
  ) : null

  const notice = showUnknownWarning ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {t(
            'EXECUTIVE.INSIGHT.AGE_GROUP.UNKNOWN_DOB_WARNING',
            'ไม่มีวันเกิด {{count}} ราย ({{pct}}%) — จึงไม่ถูกนับในกราฟ โปรดตรวจสอบข้อมูลในโปรไฟล์ผู้ป่วย',
            {
              count: unknown.toLocaleString('th-TH'),
              pct: pctUnknown.toFixed(0),
            } as any
          )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  return (
    <CategoricalBar
      title={t('EXECUTIVE.INSIGHT.AGE_GROUP.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.AGE_GROUP.TOOLTIP',
        'นับจำนวนคนไข้ (unique) แยกตามกลุ่มอายุ — อายุคำนวณจาก dateOfBirth ณ วันสิ้นสุดช่วงที่เลือก\nช่วงอายุ (buckets) สามารถปรับใน Settings > Dashboard configs\nคนไข้ที่ไม่มี dateOfBirth ในระบบจะไม่ถูกนับ — ดูในแถบเตือนด้านบน (ถ้ามี)'
      )}
      subtitle={subtitle}
      notice={notice}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.AGE_GROUP.EMPTY',
        'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      rows={rows}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
